import { Link } from "@/components/LangLink";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Star, Bandage, Waves } from "lucide-react";
import { Reveal, MaskLine, Stagger, StaggerItem } from "@/components/primitives";
import { useContent } from "@/lib/content";

export const PageHero = ({ eyebrow, title, subtitle, testid = "page-hero" }) => (
  <section className="relative overflow-hidden bg-ink pt-[120px] lg:pt-[150px] pb-16 lg:pb-24 grain-overlay" data-testid={testid}>
    <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#0F1923 0%,#1B3A6B 100%)" }} />
    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-green/20 blur-[120px]" />
    <div className="container-x relative">
      {eyebrow && (
        <Reveal>
          <span className="eyebrow text-green/90">{eyebrow}</span>
        </Reveal>
      )}
      <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] max-w-4xl">
        {String(title).split("\n").map((line, i) => (
          <MaskLine key={i} delay={0.1 + i * 0.12}>{line}</MaskLine>
        ))}
      </h1>
      {subtitle && (
        <Reveal delay={0.35}>
          <p className="mt-6 text-base lg:text-lg text-white/70 max-w-2xl leading-relaxed">{subtitle}</p>
        </Reveal>
      )}
    </div>
  </section>
);

const HEADING_TITLE_SIZES = {
  sm: "text-2xl lg:text-3xl",
  cat: "text-3xl lg:text-4xl",
  md: "text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1]",
  lg: "text-4xl lg:text-5xl leading-[1.05]",
};

export const SectionHeading = ({
  eyebrow,
  title,
  subtitle,
  intro,
  center = false,
  dark = false,
  size = "md",
  as: Tag = "h2",
  maxW = "max-w-2xl",
  className = "",
}) => (
  <div className={`${center ? "text-center mx-auto" : ""} ${maxW} ${className}`}>
    {eyebrow && (
      <Reveal>
        <span className="eyebrow">{eyebrow}</span>
      </Reveal>
    )}
    <Reveal delay={0.05}>
      <Tag className={`mt-4 font-serif ${HEADING_TITLE_SIZES[size]} font-bold ${dark ? "text-white" : "text-navy"}`}>
        {title}
      </Tag>
    </Reveal>
    {subtitle && (
      <Reveal delay={0.08}>
        <p className={`mt-2 text-base font-medium ${dark ? "text-white/80" : "text-navy/70"}`}>{subtitle}</p>
      </Reveal>
    )}
    {intro && (
      <Reveal delay={0.1}>
        <p className={`mt-5 text-base lg:text-lg leading-relaxed ${dark ? "text-white/70" : "text-muted-foreground"}`}>{intro}</p>
      </Reveal>
    )}
  </div>
);

export const Marquee = ({ items }) => (
  <div className="marquee-mask overflow-hidden py-6 border-y border-[#E7EDF2] bg-cloud/60">
    <div className="marquee-track">
      {[...items, ...items].map((t, i) => (
        <span key={i} className="flex items-center gap-4 px-8 text-navy/70">
          <span className="font-serif italic text-lg lg:text-xl">{t}</span>
          <span className="w-1.5 h-1.5 rounded-full bg-green" />
        </span>
      ))}
    </div>
  </div>
);

export const BrandsStrip = () => {
  const { COPY } = useContent();
  const brands = [
    { src: "/brands/lumaven.png", alt: "LUMAVEN", to: "/prodotti/lumaven" },
    { src: "/brands/schiebler.png", alt: "Schiebler", to: "/prodotti/schiebler" },
  ];
  return (
    <section className="py-16 lg:py-20 bg-cloud border-y border-[#E7EDF2]" data-testid="brands-strip">
      <div className="container-x">
        <Reveal className="text-center">
          <span className="eyebrow">{COPY.ui.brandsEyebrow}</span>
          <p className="mt-3 font-serif italic text-green text-lg lg:text-xl">{COPY.ui.payoff}</p>
        </Reveal>
        <Reveal className="mt-10 rounded-3xl border border-[#DDE6EC] bg-white/50 px-5 py-4 sm:px-8 sm:py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#E4EBF0]">
            {brands.map((b) => (
              <Link key={b.alt} to={b.to} data-testid={`brand-logo-${b.alt.toLowerCase()}`} className="group flex items-center justify-center py-8 sm:py-6 transition-transform duration-300 hover:-translate-y-0.5">
                <img src={b.src} alt={b.alt} className="h-11 lg:h-14 w-auto object-contain grayscale opacity-60 transition-[filter,opacity] duration-500 ease-out group-hover:grayscale-0 group-hover:opacity-100" loading="lazy" />
              </Link>
            ))}
          </div>
        </Reveal>
        <div className="mt-8 text-center">
          <Link to="/prodotti" data-testid="brands-all-products-cta" className="btn-green">
            {COPY.ui.seeAllProducts} <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export const CTASection = () => {
  const { COPY } = useContent();
  return (
    <section className="relative overflow-hidden py-20 lg:py-28" data-testid="cta-section">
      <div className="absolute inset-0" style={{ background: "linear-gradient(120deg,#0F1923 0%,#1B3A6B 55%,#1F5C48 130%)" }} />
      <div className="absolute inset-0 grain-overlay" />
      <div className="container-x relative text-center">
        <Reveal>
          <span className="eyebrow text-green/90 justify-center">{COPY.ui.ctaEyebrow}</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mx-auto">{COPY.ui.ctaTitle}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-white/70 max-w-xl mx-auto">{COPY.ui.ctaText}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/il-metodo#prenota" data-testid="cta-prenota" className="btn-green">{COPY.ui.ctaBook}</Link>
            <Link to="/#il-metodo" data-testid="cta-metodo" className="btn-light">{COPY.ui.ctaMethod}</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const BRANDCARD_LOGOS = { lumaven: "/brands/lumaven.png", schiebler: "/brands/schiebler.png" };
export const BrandCard = ({ brand }) => {
  const { COPY } = useContent();
  const isGreen = brand.theme === "green";
  const brandLogo = BRANDCARD_LOGOS[brand.id];
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-3xl p-8 lg:p-12 flex flex-col justify-between min-h-[420px] group"
      style={{ background: isGreen ? "#1F5C48" : "#12284C" }}
      data-testid={`brand-card-${brand.id}`}
    >
      <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full blur-[90px] opacity-40 transition-opacity duration-500 group-hover:opacity-60" style={{ background: isGreen ? "#2E8B6B" : "#1B3A6B" }} />
      <div className="relative">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/90 backdrop-blur">{brand.badge}</span>
          {brand.ral && <span className="inline-flex items-center rounded-full bg-green px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white">RAL</span>}
        </div>
        <h3 className="sr-only">{brand.name}</h3>
        {brandLogo ? (
          <span className="mt-8 inline-flex items-center rounded-xl bg-white px-5 py-4 shadow-sm">
            <img src={brandLogo} alt={brand.name} className="h-8 lg:h-10 w-auto object-contain" />
          </span>
        ) : (
          <span className="mt-8 block font-serif text-4xl lg:text-5xl font-bold text-white tracking-tight">{brand.name}</span>
        )}
        <p className="mt-4 text-white/70 leading-relaxed max-w-md">{brand.desc}</p>
      </div>
      <div className="relative mt-8">
        <div className="flex flex-wrap gap-2 mb-7">
          {brand.pills.map((p) => (
            <span key={p} className="rounded-full border border-white/25 px-4 py-1.5 text-xs font-medium text-white/85">{p}</span>
          ))}
        </div>
        <Link to={brand.to} data-testid={`brand-link-${brand.id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-white group/link">
          {COPY.ui.discoverBrand} {brand.name}
          <ArrowUpRight size={18} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
        </Link>
      </div>
    </motion.div>
  );
};

export const HistoryTimeline = () => {
  const { TIMELINE } = useContent();
  return (
    <div data-testid="history-timeline">
      <div className="lg:hidden relative pl-8">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-navy/15" />
        <div className="space-y-7">
          {TIMELINE.map((t) => (
            <div key={t.year} className="relative" data-testid={`timeline-${t.year}`}>
              <span className="absolute -left-8 top-1 flex h-4 w-4 items-center justify-center">
                <span className="h-4 w-4 rounded-full border-2 border-green bg-white" />
                <span className="absolute h-2 w-2 rounded-full bg-green" />
              </span>
              <div className="font-serif text-xl font-bold text-navy leading-none">{t.year}</div>
              <div className="mt-2.5 rounded-xl border border-[#E7EDF2] bg-white p-4 shadow-sm">
                <p className="text-sm text-muted-foreground leading-relaxed">{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block pb-2">
        <div>
          <div className="grid gap-4 items-end" style={{ gridTemplateColumns: `repeat(${TIMELINE.length}, minmax(0, 1fr))` }}>
            {TIMELINE.map((t) => (
              <div key={t.year} className="flex flex-col items-center">
                <div className="w-full rounded-xl border border-[#E7EDF2] bg-white p-4 shadow-sm text-center">
                  <p className="text-xs text-muted-foreground leading-relaxed">{t.text}</p>
                </div>
                <span className="mt-4 h-6 w-px bg-navy/20" />
              </div>
            ))}
          </div>
          <div className="relative">
            <div className="absolute left-0 right-0 top-2 h-px bg-navy/15" />
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${TIMELINE.length}, minmax(0, 1fr))` }}>
              {TIMELINE.map((t) => (
                <div key={t.year} className="flex flex-col items-center" data-testid={`timeline-${t.year}`}>
                  <span className="relative z-10 flex h-4 w-4 items-center justify-center">
                    <span className="h-4 w-4 rounded-full border-2 border-green bg-white" />
                    <span className="absolute h-2 w-2 rounded-full bg-green" />
                  </span>
                  <div className="mt-2 font-serif text-lg font-bold text-navy">{t.year}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Testimonials = ({ eyebrow, title, intro }) => {
  const { COPY, TESTIMONIALS } = useContent();
  return (
    <section className="py-20 lg:py-28 bg-cloud" data-testid="testimonials">
      <div className="container-x">
        <SectionHeading
          eyebrow={eyebrow || COPY.ui.testimonialsEyebrow}
          title={title || COPY.ui.testimonialsTitle}
          intro={intro || COPY.ui.testimonialsIntro}
          center
        />
        <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TESTIMONIALS.map((t, i) => (
            <StaggerItem key={t.name}>
              <div className="card-clean h-full p-7 flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 text-green">
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <Star key={s} size={16} className="fill-green" />
                  ))}
                </div>
                <p className="mt-4 text-navy/80 leading-relaxed flex-1">“{t.text}”</p>
                <div className="mt-6 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white font-serif font-bold">
                    {t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                  <div>
                    <div className="font-semibold text-navy text-sm">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

export const CheckList = ({ items, className = "" }) => (
  <ul className={`space-y-3 ${className}`}>
    {items.map((it) => (
      <li key={it} className="flex items-start gap-3 text-navy/80">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green/12 text-green">
          <Check size={12} strokeWidth={3} />
        </span>
        <span className="text-sm lg:text-base">{it}</span>
      </li>
    ))}
  </ul>
);

// Card soluzione (Home + Soluzioni): solo la soluzione, nessun marchio/logo/elenco prodotti.
const SOLUTION_BG = { navy: "#12284C", green: "#1F5C48", "green-alt": "#0E7A5F" };
const SOLUTION_GLOW = { navy: "#1B3A6B", green: "#2E8B6B", "green-alt": "#15B58C" };
export const StockingCard = ({ s }) => {
  const { COPY } = useContent();
  const bg = SOLUTION_BG[s.theme] || SOLUTION_BG.navy;
  const glow = SOLUTION_GLOW[s.theme] || SOLUTION_GLOW.navy;
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } }}
      className={`relative overflow-hidden rounded-3xl p-8 lg:p-9 flex flex-col justify-between min-h-[340px] group ${s.featured ? "ring-2 ring-green shadow-[0_34px_80px_-30px_rgba(31,92,72,0.55)] lg:-translate-y-3 lg:min-h-[380px]" : ""}`}
      style={{ background: bg }}
      data-testid={`solution-card-${s.id}`}
    >
      <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full blur-[90px] opacity-40 transition-opacity duration-500 group-hover:opacity-60" style={{ background: glow }} />
      <div className="relative">
        {s.featured && (
          <span className="mb-5 inline-flex items-center gap-1 rounded-full bg-white/15 border border-white/25 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur">
            <Star size={11} className="fill-white" /> {COPY.ui.mostChosen}
          </span>
        )}
        <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">{s.subtitle}</span>
        <h3 className={`mt-2 font-serif leading-tight font-bold text-white ${s.featured ? "text-[2.1rem] lg:text-[2.4rem]" : "text-3xl lg:text-[2.1rem]"}`}>{s.title}</h3>
        <p className="mt-4 text-sm text-white/70 leading-relaxed">{s.desc}</p>
      </div>
      <div className="relative mt-8">
        <Link to={s.to} data-testid={`solution-link-${s.id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-white group/link">
          {COPY.ui.discoverProducts}
          <ArrowUpRight size={17} className="transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
        </Link>
      </div>
    </motion.div>
  );
};

const COMPLEMENT_ICONS = { bendaggi: Bandage, pressoterapia: Waves };
export const ComplementCard = ({ c }) => {
  const { COPY } = useContent();
  const Icon = COMPLEMENT_ICONS[c.id] || Bandage;
  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}>
      <Link to={c.to} data-testid={`complement-card-${c.id}`} className="flex items-center gap-5 rounded-2xl border border-[#E7EDF2] bg-cloud px-6 py-6 transition-[border-color,transform,box-shadow] duration-300 hover:border-green hover:-translate-y-0.5 hover:shadow-[0_20px_44px_-20px_rgba(15,25,35,0.24)] group h-full">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green/12 text-green-deep transition-colors duration-300 group-hover:bg-green group-hover:text-white">
          <Icon size={22} />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-serif text-xl font-bold text-navy">{c.label}</h4>
            {c.brand && <span className="rounded-full bg-navy/5 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-navy/60">{c.brand}</span>}
          </div>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-green">
            {COPY.ui.discoverProducts}
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export const SolutionsShowcase = () => {
  const { COPY, STOCKINGS, COMPLEMENTS } = useContent();
  return (
    <div>
      <Reveal className="mb-8">
        <span className="inline-flex items-baseline gap-2.5 rounded-full border border-green/25 bg-green/10 px-6 py-3" data-testid="tre-linee-badge">
          <span className="font-serif text-3xl lg:text-4xl font-bold text-green-deep leading-none">{COPY.ui.solutionsCount}</span>
          <span className="text-sm lg:text-base font-semibold uppercase tracking-[0.18em] text-green">{COPY.ui.solutionsLabel}</span>
        </span>
      </Reveal>
      <Stagger className="grid gap-6 lg:grid-cols-3">
        {STOCKINGS.map((s) => <StockingCard key={s.id} s={s} />)}
      </Stagger>
      <Reveal className="mt-12 flex items-center gap-5">
        <span className="h-px flex-1 bg-[#E7EDF2]" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground whitespace-nowrap">{COPY.ui.complementDivider}</span>
        <span className="h-px flex-1 bg-[#E7EDF2]" />
      </Reveal>
      <Stagger className="mt-8 grid gap-4 sm:grid-cols-2 max-w-4xl mx-auto">
        {COMPLEMENTS.map((c) => <ComplementCard key={c.id} c={c} />)}
      </Stagger>
    </div>
  );
};
